import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild, Renderer2, Inject } from '@angular/core';
import { CommonModule, DOCUMENT } from '@angular/common';
import { trigger, style, animate, transition } from '@angular/animations';
import { Router } from '@angular/router';
import { CareerService } from '../../../services/career.service';
import { Job } from '../job.interface';
import { FaqComponent } from '../../../shared/faq/faq.component';
import { FaqItem } from '../../../models/faq.model';

interface HiringStep {
  icon: string;
  title: string;
  description: string;
}
@Component({
  selector: 'app-career-at-kalpita',
  imports: [CommonModule, FaqComponent],
  templateUrl: './career-at-kalpita.component.html',
  styleUrl: './career-at-kalpita.component.scss',

  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(15px)' }),
        animate('500ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ])
  ]
})
export class CareerAtKalpitaComponent implements OnInit, AfterViewInit {
  private jsonLdScript: any;
  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  priorityJobs: Job[] = [];
  private forcePlayHeroVideo() {
    const video = this.heroVideo?.nativeElement;
    if (!video) return;

    video.muted = true;           // required for autoplay
    video.playsInline = true;

    const play = () => {
      video.play().catch(() => {
        // retry if browser blocks
        setTimeout(() => video.play().catch(() => { }), 300);
      });
    };

    // Ensure it runs after Angular rendering
    setTimeout(play, 100);
  }
  perksList = [
    {
      title: 'Quarterly Performance Bonus',
      description: '3% Additional quarterly bonuses based on individual performance rating.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/performance_bonus.svg'
    },
    {
      title: 'Health & Safety Benefits',
      description: 'Group medical coverage up to ₹3 LPA and personal accidental insurance up to ₹25 LPA.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/health_safety.svg'
    },
    {
      title: 'Celebrations & Special Moments',
      description: 'Monthly birthday celebrations, gifts for weddings and newborns, festive celebration with gifts.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/celebrations.svg'
    },
    {
      title: 'Learning & Development',
      description: 'Advance your skills with one certification per year and receive up to ₹5,000 reimbursement.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/learning-and-development.svg'
    },
    {
      title: 'Team Bonding & Culture',
      description: 'Quarterly team lunch allowances, relaxation activities (SPARK), breakfast & lunch, weekly potlucks.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/teambonding_culture.svg'
    },
    {
      title: 'Recognition & Engagement',
      description: 'Regular appreciation to recognize employee contribution & achievements and bi-monthly Townhalls.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/recognition_engagement.svg'
    },
    {
      title: 'Employee Wellbeing',
      description: 'Counseling sessions and a flexible work-life balance culture that prioritizes employees’ wellbeing.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/employee-well-being.svg'
    },
    {
      title: 'CSR & Community Impact',
      description: 'Opportunities to contribute to meaningful Corporate Social Responsibility initiatives and community services.',
      icon: 'assets/CareersAtKalpita/empPerksBenefits/csr.svg'
    }
  ];

  careerFaqs: FaqItem[] = [
    {
      question: 'How do I apply for a job at Kalpita?',
      answer: `You can apply directly through our Open Positions page by selecting an open role and 
      submitting your details and resume. Based on the requirement match, our recruiter will reach 
      out to you.`
    },
    {
      question: 'How does the hiring process work?',
      answer: `The hiring process at Kalpita follows a structured 6-step 
      approach: Apply → Screening → Two Techno-Functional Interviews → HR Round → Offer & Onboarding. 
      This clear process ensures fairness, transparency, and a smooth candidate experience.`
    },
    {
      question: 'How long does the hiring process take?',
      answer: `On average, it takes 2–3 weeks from application to offer.`
    },
    {
      question: 'What benefits do employees get at Kalpita?',
      answer: `We provide competitive salaries, quarterly performance bonuses, health insurance, 
      sponsored certifications, team engagement activities, and wellbeing programs.`
    },
    {
      question: 'Is Kalpita a good place to grow my career?',
      answer: `Yes, we prioritize internal promotions, upskilling programs, and diverse project 
      exposure to accelerate career growth.`
    }
  ];

  hiringSteps: HiringStep[] = [
    {
      icon: 'assets/CareersAtKalpita/apply.png',
      title: 'Apply',
      description: 'Submit your resume through our open positions page. Our recruitment team carefully reviews every application within a few business days.'
    },
    {
      icon: 'assets/CareersAtKalpita/techround.png',
      title: 'T1 - Technical Round',
      description: 'Your first round focuses on assessing your problem-solving skills, coding ability, and technical knowledge relevant to the position. Expect questions on real-world scenarios, algorithms, and role-specific technologies. (May differ based on role and requirement)'
    },
    {
      icon: 'assets/CareersAtKalpita/hr.png',
      title: 'HR Round',
      description: 'Meet with our HR to discuss your career aspirations, culture fit, and compensation details. We value transparency, so this is also your chance to ask questions about Kalpita.'
    },
    {
      icon: 'assets/CareersAtKalpita/screening.png',
      title: 'Screening',
      description: 'If your profile matches our needs, you’ll be invited for an initial screening call. This short discussion helps us understand your background, interests, and career goals.'
    },
    {
      icon: 'assets/CareersAtKalpita/techround.png',
      title: 'T2 - Functional Round',
      description: 'In this round, we evaluate your functional expertise, understanding of business processes, and applied problem-solving. It also assesses your communication, collaboration, and approach to role-specific challenges. (May differ based on role and requirement)'
    },
    {
      icon: 'assets/CareersAtKalpita/offer_onboarding.png',
      title: 'Offer & Onboarding',
      description: 'If selected, you’ll receive a formal offer along with clear next steps. Our onboarding process ensures you settle in quickly, with all the resources, mentorship, and support you need to succeed from day one.'
    }
  ];

  ngAfterViewInit(): void {
    this.forcePlayHeroVideo();
    const cards = document.querySelectorAll('.hiring-step-card');
    cards.forEach(card => {
      const cardEl = card as HTMLElement;

      const mouseMoveHandler = (e: MouseEvent) => {
        const rect = cardEl.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        const xc = rect.width / 2;
        const yc = rect.height / 2;
        const maxRotate = 12; // subtle tilt
        const rotateY = ((x - xc) / xc) * maxRotate;
        const rotateX = ((yc - y) / yc) * maxRotate;
        cardEl.style.transform = `rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02,1.02,1.02)`;
      };

      const mouseLeaveHandler = () => {
        cardEl.style.transform = 'rotateX(0deg) rotateY(0deg) scale3d(1,1,1)';
      };

      const mouseEnterHandler = () => {
        cardEl.style.transition = 'transform 0.18s cubic-bezier(.3,1.3,.45,1)';
      };

      cardEl.addEventListener('mousemove', mouseMoveHandler as EventListener);
      cardEl.addEventListener('mouseleave', mouseLeaveHandler as EventListener);
      cardEl.addEventListener('mouseenter', mouseEnterHandler as EventListener);

      // Disable effect on touch devices (keeps UX clean on mobile)
      if (window.matchMedia('(hover: none) and (pointer: coarse)').matches) {
        cardEl.removeEventListener('mousemove', mouseMoveHandler as EventListener);
        cardEl.removeEventListener('mouseleave', mouseLeaveHandler as EventListener);
        cardEl.removeEventListener('mouseenter', mouseEnterHandler as EventListener);
        cardEl.style.transform = 'none';
      }
    });
  }
  //LifeAtKalpita
  lifeAtKalpitaCards = [
    {
      title: 'Life at Kalpita',
      img: 'assets/CareersAtKalpita/LifeAtKalpita.png',
      route: '/gallery/life-at-kalpita'
    },
    {
      title: 'Learn With Kalpita',
      img: 'assets/CareersAtKalpita/LearnWithKalpita.png',
      route: '/gallery/learn-with-kalpita'
    },
    {
      title: 'Corporate Social Responsibility',
      img: 'assets/CareersAtKalpita/CorporateSocialResponsibility.png',
      route: '/aboutcompany/kalpita-csr'
    }
  ];

  navigateTo(path: string) {
    this.router.navigate([path]);
    window.scrollTo(0, 0);
  }

  constructor(private careerService: CareerService, private router: Router, @Inject(DOCUMENT) private _document: Document, private renderer: Renderer2) { }

  ngOnInit(): void {
    this.injectSchemaMarkup();
    // Fetch jobs and take top 3
    this.careerService.getJob().subscribe({
      next: (result: Job[]) => {
        // You might want to filter or sort here if "priority" means something specific
        // For now, we take the first 3
        this.priorityJobs = result.slice(0, 3);
      },
      error: (err) => {
        console.error('Error fetching jobs:', err);
      }
    });
  }
  ngOnDestroy(): void {
    this.removeSchemaMarkup();
  }
  injectSchemaMarkup(): void {
    const careerPageSchema = {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "WebPage",
          "@id": "https://www.kalpitatechnologies.com/careers/career-at-kalpita#webpage",
          "url": "https://www.kalpitatechnologies.com/careers/career-at-kalpita",
          "name": "Careers at Kalpita Technologies | AI Software Jobs in Bangalore & USA",
          "description": "Explore careers at Kalpita Technologies, a Great Place to Work-Certified, AI-focused software company.",
          "isPartOf": { "@id": "https://www.kalpitatechnologies.com/#website" },
          "primaryImageOfPage": { "@type": "ImageObject", "url": "https://www.kalpitatechnologies.com/assets/CareersAtKalpita/careerAtKalpita.webm" },
          "inLanguage": "en-US"
        },
        {
          "@type": "BreadcrumbList",
          "@id": "https://www.kalpitatechnologies.com/careers/career-at-kalpita#breadcrumbs",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.kalpitatechnologies.com/" },
            { "@type": "ListItem", "position": 2, "name": "Careers", "item": "https://www.kalpitatechnologies.com/careers" },
            { "@type": "ListItem", "position": 3, "name": "Career at Kalpita", "item": "https://www.kalpitatechnologies.com/careers/career-at-kalpita" }
          ]
        },
        {
          "@type": "EmployerAggregateRating",
          "@id": "https://www.kalpitatechnologies.com/careers/career-at-kalpita#employer-rating",
          "itemReviewed": {
            "@type": "Organization",
            "@id": "https://www.kalpitatechnologies.com/#organization",
            "name": "Kalpita Technologies"
          },
          "ratingValue": "4.5",
          "bestRating": "5",
          "worstRating": "1",
          "ratingCount": "3"
        },
        {
          "@type": "HowTo",
          "@id": "https://www.kalpitatechnologies.com/careers/career-at-kalpita#hiring-process",
          "name": "Kalpita Hiring Process: 6 Simple Steps to Get Hired",
          "totalTime": "P2W",
          "step": [
            { "@type": "HowToStep", "position": 1, "name": "Apply", "text": "Submit your resume through the Open Positions page." },
            { "@type": "HowToStep", "position": 2, "name": "Screening", "text": "Initial screening call to discuss background and career goals." },
            { "@type": "HowToStep", "position": 3, "name": "T1 Technical Round", "text": "Assessing problem-solving and coding ability." },
            { "@type": "HowToStep", "position": 4, "name": "T2 Functional Round", "text": "Evaluating domain and functional expertise." },
            { "@type": "HowToStep", "position": 5, "name": "HR Round", "text": "Discuss career aspirations and culture fit." },
            { "@type": "HowToStep", "position": 6, "name": "Offer & Onboarding", "text": "Formal offer and onboarding support." }
          ]
        },
        {
          "@type": "FAQPage",
          "@id": "https://www.kalpitatechnologies.com/careers/career-at-kalpita#faq",
          "mainEntity": [
            {
              "@type": "Question",
              "name": "How do I apply for a job at Kalpita Technologies?",
              "acceptedAnswer": { "@type": "Answer", "text": "Apply directly through the Open Positions page at kalpitatechnologies.com/careers/kalpita-open-positions." }
            },
            {
              "@type": "Question",
              "name": "How does the hiring process work at Kalpita Technologies?",
              "acceptedAnswer": { "@type": "Answer", "text": "A structured 6-step approach: Apply, Screening, T1 Technical, T2 Functional, HR Round, and Onboarding." }
            }
          ]
        }
      ]
    };

    this.jsonLdScript = this.renderer.createElement('script');
    this.jsonLdScript.type = 'application/ld+json';
    this.jsonLdScript.text = JSON.stringify(careerPageSchema);
    this.renderer.appendChild(this._document.head, this.jsonLdScript);
  }

  removeSchemaMarkup(): void {
    if (this.jsonLdScript) {
      this.renderer.removeChild(this._document.head, this.jsonLdScript);
      this.jsonLdScript = null;
    }
  }
  // Navigation handlers
  viewOpenPositions() {
    this.router.navigate(['/careers/kalpita-open-positions']);
  }

  applyForJob(jobId: string | number) {
    this.router.navigate(['/careers/applyposition', jobId]);
  }


  // slider/dot active index (keeps the same initial active dot as your original markup)
  activeLifeDot = 0;

  // optional: when user clicks a dot (keeps view exactly same unless user clicks)
  setActiveLifeDot(i: number) {
    this.activeLifeDot = i;
  }
}